import * as React from 'react';
import Select, { SelectValue } from '../../../../Components/Base/Select/Select';
import {
	useLazyRootWorkplaceByChildQuery,
	useLazyWorkplacesForSelectQuery,
} from '../../../../graphql/graphql';
import { FieldError } from 'react-hook-form';
import { useState } from 'react';
import { useAppContext } from '../../../../Services/AppContext';
import useTranslation from '../../../../Hooks/useTranslation';
import { useEffectAsync } from '../../../../helpers';
import useApiClient from '../../../Base/Hooks/useApiClient';
import useLoading from '../../../Base/Hooks/useLoading';
import useSelectValueGuard from '../../../Base/Hooks/useSelectValueGuard';

interface Props {
	onChange?: (value: SelectValue) => void;
	value?: SelectValue;
	error?: FieldError;
	portal?: boolean;
	workplaceType?: string;
	workplaceTypes?: string[];
	filterByRootWorkplaceId?: boolean;
	excludedIds?: string[];
	disabled?: boolean;
	managed?: boolean;
}

interface QueryArgs {
	take?: number;
	type?: string;
	rootWorkplaceId?: string;
	managed?: boolean;
}

const WorkplaceSelect: React.FC<Props> = props => {
	const {
		value,
		onChange,
		portal = false,
		workplaceType = 'normal',
		workplaceTypes,
		filterByRootWorkplaceId,
		excludedIds,
		disabled,
		managed,
	} = props;
	const { t } = useTranslation();
	const { displayGQLError } = useApiClient();
	const { workplaceId } = useAppContext();
	const { isLoading, setIsLoading, RenderLoading } = useLoading(true);
	const [options, setOptions] = useState<SelectValue[]>([]);
	const [getWorkplaces] = useLazyWorkplacesForSelectQuery();
	const [getRootWorkplace] = useLazyRootWorkplaceByChildQuery();

	useEffectAsync(async () => {
		setIsLoading(true);
		const items: SelectValue[] = [];
		const queryArgs: QueryArgs = {
			take: 999,
			type: workplaceTypes ? undefined : workplaceType,
			managed: managed,
		};

		if (filterByRootWorkplaceId) {
			const response = await getRootWorkplace({
				workplaceId,
			});
			displayGQLError(response);
			queryArgs.rootWorkplaceId = response?.data?.workplace?.rootWorkplace?.id;
		}

		const response = await getWorkplaces(queryArgs);

		displayGQLError(response);

		if ('data' in response) {
			for (const workplace of response.data?.workplaces?.data || []) {
				if (workplace && !excludedIds?.some(item => item === workplace.id)) {
					if (
						workplaceTypes &&
						workplaceTypes?.length > 0 &&
						workplace.type &&
						!workplaceTypes.includes(workplace.type)
					) {
						continue;
					}
					items.push({
						value: workplace.id,
						label: workplace.doctorDisplayName || '',
					});
				}
			}
		}

		setOptions(items);
		setIsLoading(false);
	}, [excludedIds, workplaceTypes]);

	useSelectValueGuard({ onChange, options, value });

	if (isLoading) {
		return <RenderLoading />;
	}

	return (
		<Select
			options={options}
			{...props}
			onChange={onChange}
			value={value}
			portal={portal}
			placeholder={t('base.shared.pick')}
			disabled={disabled}
			showErrorMessage={false}
		/>
	);
};

export default WorkplaceSelect;
